'use client';

import { cn } from '@workspace/ui/lib';
import { OTPInput, OTPInputContext } from 'input-otp';
import { MinusIcon } from 'lucide-react';
import * as React from 'react';

function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
  placeholder?: string;
}) {
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn('flex items-center gap-2 has-disabled:opacity-50', containerClassName)}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn('flex items-center gap-2', className)}
      {...props}
    />
  );
}

function InputOTPSlot({
  index,
  className,
  placeholder = '0',
  ariaInvalid = false,
  ...props
}: React.ComponentProps<'div'> & {
  index: number;
  placeholder?: string;
  ariaInvalid?: boolean;
}) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      aria-invalid={ariaInvalid}
      className={cn(
        'aria-invalid:border-destructive! text-primary data-[active=true]:aria-invalid:border-destructive! border-input border-grey-400 data-[active=true]:border-primary relative flex h-[48px] w-[38.5px] items-center justify-center rounded-2xl border bg-transparent px-4 py-3 text-base shadow-xs transition-all outline-none data-[active=true]:z-10 sm:w-[50px] dark:text-neutral-100 dark:data-[active=true]:border-neutral-100',
        className
      )}
      {...props}
    >
      {char || (!isActive && <span className="text-grey-500">{placeholder}</span>)}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-primary dark:bg-neutral-100 h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <MinusIcon />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
